import React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { API } from "api";
import { UTILS } from "utils";
import AppModal from "components/ui/AppModal";
import AppInput from "components/ui/AppInput";
import AppSelect from "components/ui/AppSelect";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import { calculateDeliveryTimeInMins } from "features/shipping_methods/helpers";
import { errorHandler } from "api/api/errorHandler";

const initialFormState = {
  minOrder: "",
  deliveryFee: "",
  deliveryTime: {
    Hours: 0,
    Minutes: 0,
    Days: 0,
  },
};

const AddDeliveryAreaModal = ({ title, open, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const { selectedStore, customAllAreas, areaData } = useSelector(
    (state) => state.AreasReducer
  );

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [selectedAreaState, setState] = useState(initialFormState);

  useEffect(() => {
    if (!open) return;
    setState(initialFormState);
    setSearchTerm("");
    setSelectedAreaIds([]);
  }, [open]);

  const cityLookup = useMemo(() => {
    const lookup = new Map();
    areaData?.forEach((country) => {
      country?.cities?.forEach((city) => {
        lookup.set(city?._id, city);
      });
    });
    return lookup;
  }, [areaData]);

  const availableAreas = useMemo(() => {
    if (!Array.isArray(customAllAreas)) return [];
    return customAllAreas
      .filter((area) => !area?.isAdded)
      .map((area) => ({
        ...area,
        cityInfo: cityLookup.get(area?.cityID),
      }));
  }, [customAllAreas, cityLookup]);

  const filteredAreas = useMemo(() => {
    const term = searchTerm?.trim()?.toLowerCase();
    if (!term) return availableAreas;
    return availableAreas.filter((area) => {
      const areaEn = area?.areaNameEN || "";
      const areaAr = area?.areaNameAR || "";
      const cityEn = area?.cityInfo?.cityNameEN || "";
      const cityAr = area?.cityInfo?.cityNameAR || "";
      return [areaEn, areaAr, cityEn, cityAr].some((value) =>
        value.toLowerCase().includes(term)
      );
    });
  }, [availableAreas, searchTerm]);

  const selectedAreas = useMemo(() => {
    if (selectedAreaIds.length === 0) return [];
    const ids = new Set(selectedAreaIds);
    return availableAreas.filter((area) => ids.has(area?._id));
  }, [availableAreas, selectedAreaIds]);

  const daysOptions = useMemo(
    () =>
      Array.from({ length: 8 }, (_, value) => ({
        label: `${value} ${t("Days")}`,
        value,
      })),
    [t]
  );
  const hourOptions = useMemo(
    () =>
      Array.from({ length: 24 }, (_, value) => ({
        label: `${value} ${t("Hours")}`,
        value,
      })),
    [t]
  );
  const minuteOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const value = index * 5;
        return {
          label: `${value} ${t("Minutes")}`,
          value,
        };
      }),
    [t]
  );

  const handleValues = (key, value) =>
    setState((prev) => ({ ...prev, [key]: value }));

  const handleTimeValues = (key, value) =>
    setState((prev) => ({
      ...prev,
      deliveryTime: {
        ...prev.deliveryTime,
        [key]: Number(value || 0),
      },
    }));

  const toggleAreaSelection = (areaId) => {
    setSelectedAreaIds((prev) =>
      prev.includes(areaId)
        ? prev.filter((id) => id !== areaId)
        : [...prev, areaId]
    );
  };

  const saveArea = async () => {
    if (!selectedStore?.value) {
      UTILS.errorNotification(t("Please select store"));
      return;
    }
    if (!selectedAreaState.minOrder || Number(selectedAreaState.minOrder) < 0) {
      UTILS.errorNotification(t("Please enter minimum order amount"));
      return;
    }
    if (
      !selectedAreaState.deliveryFee ||
      Number(selectedAreaState.deliveryFee) < 0
    ) {
      UTILS.errorNotification(t("Please enter delivery fee"));
      return;
    }
    if (selectedAreas.length === 0) {
      UTILS.errorNotification(t("Please select at least one area"));
      return;
    }
    const deliveryTimeInMins = calculateDeliveryTimeInMins({
      deliveryTime: selectedAreaState.deliveryTime,
    });
    if (deliveryTimeInMins <= 0) {
      UTILS.errorNotification(t("Please choose delivery time"));
      return;
    }

    const userDetail = UTILS.getCurrentUser();
    const objectArray = selectedAreas.map((area) => ({
      cityID: area.cityID,
      areaID: area._id,
      countryID: area.countryID,
      storeId: selectedStore.value,
      brandId: userDetail.brandId,
    }));

    try {
      setLoading(true);
      const result = await API.ADD_STORE_AREA({
        areas: objectArray,
        minOrder: Number(selectedAreaState.minOrder),
        deliveryFee: Number(selectedAreaState.deliveryFee),
        deliveryTime: Number(deliveryTimeInMins),
        token: userDetail.token,
        language: currentLanguage,
      });
      if (result?.success === 1) {
        UTILS.successNotification(t(result?.message || "Area added"));
        await onSaved?.();
        onClose?.();
      } else {
        UTILS.errorNotification(errorHandler(result));
      }
    } catch (error) {
      UTILS.errorNotification(t("Please try again later!"));
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <AppOutlineButton variant="secondary" onClick={onClose} disabled={loading}>
        {t("Cancel")}
      </AppOutlineButton>
      <AppButton variant="primary" onClick={saveArea} loading={loading}>
        {t("Add Area")}
      </AppButton>
    </div>
  );

  return (
    <AppModal title={title} open={open} onClose={onClose} width={650} footer={footer}>
      <div className="grid grid-cols-2 gap-4">
        <AppInput
          label={t("Min. Basket Value")}
          value={selectedAreaState.minOrder}
          onInput={(e) =>
            handleValues("minOrder", UTILS.NumberConversion(e.target.value))
          }
        />
        <AppInput
          label={t("Delivery Fee")}
          value={selectedAreaState.deliveryFee}
          onInput={(e) =>
            handleValues("deliveryFee", UTILS.NumberConversion(e.target.value))
          }
        >
          <span>AED</span>
        </AppInput>
      </div>
      <div className="my-3 text-lg font-bold">{t("Delivery Time")}</div>
      <div className="grid grid-cols-3 gap-4">
        <AppSelect
          placeholder={t("Select Days")}
          value={selectedAreaState.deliveryTime.Days}
          options={daysOptions}
          onChange={(value) => handleTimeValues("Days", value)}
        />
        <AppSelect
          placeholder={t("Select Hours")}
          value={selectedAreaState.deliveryTime.Hours}
          options={hourOptions}
          onChange={(value) => handleTimeValues("Hours", value)}
        />
        <AppSelect
          placeholder={t("Select Minutes")}
          value={selectedAreaState.deliveryTime.Minutes}
          options={minuteOptions}
          onChange={(value) => handleTimeValues("Minutes", value)}
        />
      </div>
      <div className="my-3 text-lg font-bold">
        <span className="mb-2 block">{t("Search Area Name")}</span>
        <AppSearchInput
          value={searchTerm}
          placeholder={t("Search")}
          onInput={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 p-2">
        {filteredAreas.length === 0 ? (
          <div className="p-2 text-sm text-gray-500">{t("No areas found")}</div>
        ) : (
          filteredAreas.map((area) => {
            const isSelected = selectedAreaIds.includes(area?._id);
            const areaName =
              currentLanguage === "AR" ? area?.areaNameAR : area?.areaNameEN;
            const cityName =
              currentLanguage === "AR"
                ? area?.cityInfo?.cityNameAR
                : area?.cityInfo?.cityNameEN;
            return (
              <button
                key={area?._id}
                type="button"
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left hover:bg-gray-50"
                onClick={() => toggleAreaSelection(area?._id)}
              >
                <div>
                  <div className="text-sm font-medium text-gray-800">{areaName}</div>
                  <div className="text-xs text-gray-500">{cityName}</div>
                </div>
                <span
                  className={`block h-5 w-5 rounded border ${
                    isSelected ? "bg-blue-500 border-blue-500" : "bg-white border-gray-300"
                  }`}
                />
              </button>
            );
          })
        )}
      </div>
    </AppModal>
  );
};

export default AddDeliveryAreaModal;